import {
  convertQueryCommandToQueryString, IQueryCommandOptions, Equal, Not, mergeQueryCommandOptions,
} from '@llws/dynamic-router-utils';
import {
  ITourUserSettings,
} from '@llws/lift-entity-interfaces';
import { IGroups } from '@llws/platform-shared-types/types/db/auth';
import { IClientUsers } from '@llws/platform-shared-types/types/api/perms-api';
import { EPropertyMode } from '@llws/platform-shared-types/types/auth';
import {
  ISelectOption,
  IUserGroupUser,
} from '@/shared';
import { StoreBaseImplementationV2 } from '@/shared/classes/store-base-implementation-v2';
import { adminRequest } from '@/shared/request-configs/admin-request';
import vm from '@/main';
import { IAction, IStateStrict } from '@/shared/interfaces/store-base';

interface ITourUserSettingsState {
  agentsOptionsForClient: Array<ISelectOption>,
  tourUserSettings: Array<ITourUserSettings>,
  tourUserSettingsById: Array<ITourUserSettings>,
  requestedYearMonth: string,
  requestedDuration: number,
  agentsWithModule: IUserGroupUser[],
}

// Base store configuration
const agentGatewayResourcePathAgent: string = '/tour-api-agent';
const adminGatewayResourcePathAdmin: string = '/tour-api-admin';
const listViewName: string = '';
const showLogging: boolean = false;

const defaultGetListFilters: IQueryCommandOptions = {};

const defaultGetSingleFilters: IQueryCommandOptions = {};

// Instantiate the base store with the params
const base = new StoreBaseImplementationV2<ITourUserSettings>({
  adminGatewayResourcePath: adminGatewayResourcePathAdmin,
  defaultGetSingleFilters,
  defaultGetListFilters,
  listViewName,
  showLogging,
});

const agentsOptionsForClientQueryString: IQueryCommandOptions = {
  relations: [
    {
      path: 'user',
      alias: 'u',
    },
  ],
  select: [
    'id',
    'userId',
    'clientId',
    'status',
  ],
  limit: 9999,
};

const tourUserSettingsFilters: IQueryCommandOptions = {
  relations: [
    {
      alias: 'user',
      path: 'user',
    },
    {
      alias: 'client',
      path: 'client',
    },
  ],
  where: [Not(Equal('status', 'deleted'))],
  limit: 999,
};

export default {
  namespaced: base.namespaced,

  state: {
    ...base.state,
    agentsOptionsForClient: [] as Array<ISelectOption>,
    requestedYearMonth: '' as string,
    requestedDuration: 0 as number,
    tourUserSettings: [] as Array<ITourUserSettings>,
    tourUserSettingsById: {} as ITourUserSettings,
    agentsWithModule: [] as IUserGroupUser[],
  },

  getters: {
    ...base.getters,
    agentsOptionsForClient(state: ITourUserSettingsState): ISelectOption[] {
      return state.agentsOptionsForClient;
    },

    tourUserSettings(state: ITourUserSettingsState) { return state.tourUserSettings ? state.tourUserSettings : []; },

    tourUserSettingsById(state: ITourUserSettingsState): ITourUserSettings[] { return state.tourUserSettingsById; },

    agentsWithModule(state: ITourUserSettingsState): IUserGroupUser[] { return state.agentsWithModule; },

  },

  mutations: {
    ...base.mutations,
    setAgentsOptionsForClient: (state: ITourUserSettingsState, payload: Array<ITourUserSettings>) => {
      const options: ISelectOption[] = (Array.isArray(payload) ? payload : [])
        .filter((tourUser) => tourUser.user)
        .map((tourUser) => ({
          text: `${tourUser.user!.firstName} ${tourUser.user!.lastName}`,
          value: tourUser.userId,
        }));
      state.agentsOptionsForClient = options;
    },
    setTourUserSettings(state: ITourUserSettingsState, payload: Array<ITourUserSettings>) {
      state.tourUserSettings = payload;
    },
    setTourUserSettingsById(state: ITourUserSettingsState, payload: Array<ITourUserSettings>) {
      state.tourUserSettingsById = payload;
    },
    setAgentsWithModule(state: ITourUserSettingsState, payload: IUserGroupUser[]) {
      state.agentsWithModule = payload || [];
    },
  },

  actions: {
    ...base.actions,
    getTourAgents: async (ctx: IAction<IStateStrict<IUserGroupUser[]>>) => {
      const response = await adminRequest.get(`${adminGatewayResourcePathAdmin}/tour-agents`);
      ctx.commit('setAgentsWithModule', response.data);
    },
    getTourUserSettings: async (ctx: IAction<IStateStrict<ITourUserSettings>>, payload: IQueryCommandOptions) => {
      const filters: IQueryCommandOptions = mergeQueryCommandOptions([tourUserSettingsFilters, payload || {}]);
      const response = await adminRequest.get(`${vm()?.$rsGlobals.$hasToursRestricted() ? agentGatewayResourcePathAgent : adminGatewayResourcePathAdmin}/tour_user_settings${convertQueryCommandToQueryString(filters)}`);
      ctx.commit('setTourUserSettings', response.data);
      return response.data;
    },
    /**
     * Get Tour User Settings By Id
     * @param ctx
     * @param id
     * @returns
     */
    getTourUserSettingsById: async (ctx: IAction<IStateStrict<ITourUserSettings>>, id: number) => {
      const filters: IQueryCommandOptions = {
        ...tourUserSettingsFilters,
        relations: [{ path: 'user', alias: 'user' }],
        where: [
          Equal('userId', id),
        ],
      };
      const response = await adminRequest.get(`${vm()?.$rsGlobals.$hasToursRestricted() ? agentGatewayResourcePathAgent : adminGatewayResourcePathAdmin}/tour_user_settings${convertQueryCommandToQueryString(filters)}`);
      ctx.commit('setTourUserSettingsById', response.data);
      return response.data;
    },

    getAgentsOptionsForClient: async (ctx: IAction<IStateStrict<ITourUserSettings>>, payload: IQueryCommandOptions) => {
      const queryString: IQueryCommandOptions = payload ? { ...agentsOptionsForClientQueryString, ...payload } : agentsOptionsForClientQueryString;
      const response = await adminRequest.get(`${vm()?.$rsGlobals.$hasToursRestricted() ? agentGatewayResourcePathAgent : adminGatewayResourcePathAdmin}/tour_user_settings${convertQueryCommandToQueryString(queryString)}`);
      ctx.commit('setAgentsOptionsForClient', response.data);
    },
    insertUserAgentSettings: async (_ctx: IAction<IStateStrict<ITourUserSettings>>, userAgentSettings: Partial<ITourUserSettings>) => {
      const result = await adminRequest.post(`${vm()?.$rsGlobals.$userHasAtLeastOneAccess('tour_admins', 'read') ? adminGatewayResourcePathAdmin : agentGatewayResourcePathAgent}/tour_user_settings`, userAgentSettings);
      return result;
    },
    updateUserAgentSettings: async (_ctx: IAction<IStateStrict<ITourUserSettings>>, userAgentSettings: Partial<ITourUserSettings>) => {
      const result = await adminRequest.put(`${vm()?.$rsGlobals.$userHasAtLeastOneAccess('tour_admins', 'read') ? adminGatewayResourcePathAdmin : agentGatewayResourcePathAgent}/tour_user_settings/${userAgentSettings.id}`, userAgentSettings);
      return result;
    },
  },
};
